import { DEFAULT_HANDLERS, createDefaultProviders, createDefaultRoutes } from './defaults';
import { hrefProvider } from './providers/href';
import { applyProviderChain, hasHrefFallback } from './utils';

import type { ActionHandler, ActionHandlerConfig, RouteHandlerFn } from './types';

/**
 * NOL service 의 표준 action dispatch factory. `Header.onAction` 에 그대로 전달 가능한
 * `handle` 함수를 반환한다.
 *
 * ## 동작 흐름 (한 click 당)
 *
 * 1. `e.preventDefault()` — sync. a tag default action 즉시 차단.
 * 2. `handlers[action]` 매칭되면 await — log / analytics 등 side effect 완료 보장.
 * 3. `routes[action]` 매칭되면 provider chain 으로 navigate.
 *    - 첫 `isAvailable() === true` provider 의 `navigate(url)` 호출.
 *    - 모두 unavailable 이면 silent no-op.
 * 4. handler 가 `throw` 하면 navigate skip — *abort 시그널*.
 *
 * `handlers` 와 `routes` 는 **상호 배타가 아님** — 같은 action 에 둘 다 있으면
 * *handler 먼저 (await), 그 다음 navigate* 순차 실행. 일반적으로 `handlers` 는
 * log/analytics 만 담당 (navigate 책임 X).
 *
 * ## 자동 적용되는 default
 *
 * 호스트가 `config` 의 entry 를 명시 안 한 경우 다음 default 가 *자동* 적용:
 *
 * | config entry | 미명시 시 default | 출처 |
 * |---|---|---|
 * | `routes` | `createDefaultRoutes(env)` — env-aware full URL 매핑 | `defaults.ts` |
 * | `providers` | `createDefaultProviders()` — `[nolBridgeProvider]` | `defaults.ts` |
 * | `handlers` | `DEFAULT_HANDLERS` 자동 merge (호스트가 명시 시 spread override) | `defaults.ts` |
 *
 * `providers` 끝에 `hrefProvider` 가 *항상 auto-append* (이미 포함 시 중복 안 함).
 * → webview (`nolBridgeProvider.isAvailable() === true`) 환경에선 `NolBridge.openWebView`,
 *   일반 웹에선 `window.location.href` 로 자동 분기.
 *
 * ## 호스트 override 패턴
 *
 * - **env 만 명시** (가장 흔한 케이스 — providers / routes default 사용):
 *   ```ts
 *   const handle = createActionHandler({ env });
 *   <Header onAction={handle}>
 *   ```
 *
 * - **handlers 에 log/analytics 추가** — navigate 는 routes default 가 처리:
 *   ```ts
 *   createActionHandler({
 *     env,
 *     handlers: {
 *       my: async () => { await trackClick('my'); },
 *       // back default (NolBridge.closeWebView or window.history.back) 는 자동 유지
 *     },
 *   });
 *   ```
 *
 * - **일부 routes 만 override** (env 별 origin 은 default 유지):
 *   ```ts
 *   createActionHandler({
 *     env,
 *     routes: { ...createDefaultRoutes(env), my: '/custom-mypage' },
 *   });
 *   ```
 *
 * - **back default 동작 override** — 호스트가 자체 라우터로 처리:
 *   ```ts
 *   createActionHandler({
 *     handlers: { back: () => router.back() },
 *   });
 *   ```
 *
 * - **동적 URL navigate** (서버 응답 / 외부 link 등 — routes table 우회):
 *   ```ts
 *   const handle = createActionHandler({ env });
 *   // ...
 *   handle.navigate(meta.action.web);
 *   ```
 *
 * ## `DEFAULT_HANDLERS` 자세히
 *
 * - `back`: NolBridge.isEnabled 면 `NolBridge.closeWebView()`, 아니면 `window.history.back()`.
 *   호스트가 `handlers.back` 명시하면 override.
 *
 * ## `createDefaultRoutes(env)` 자세히
 *
 * env 별로 다음 키를 full URL 로 매핑 (`logo`, `my`, `wishlist`, `cart`, `recent`).
 * env: `prod | stage | dev | qa | local` — `{env}-nol.yanolja.com` / `{env}-platform-site.yanolja.com`
 * 패턴 (prod 만 prefix 없음, local 은 dev origin fallback).
 */
export function createActionHandler(config: ActionHandlerConfig = {}): ActionHandler {
  const userProviders = config.providers ?? createDefaultProviders();
  // hrefProvider 는 항상 마지막 fallback. 호스트가 명시 안 한 경우 자동 append.
  const providers = hasHrefFallback(userProviders) ? userProviders : [...userProviders, hrefProvider];

  // routes 미명시 시 env 기반 default 자동 사용. 호스트가 routes 명시한 경우 env 는 무시됨.
  const routes = config.routes ?? createDefaultRoutes(config.env ?? 'prod');

  // Partial<Record> 로 타입 명시 — 인덱스 lookup 결과를 `RouteHandlerFn | undefined` 로 평가시켜
  // `if (handler)` narrowing 이 실제 runtime (key 미존재 → undefined) 과 일치하게 한다.
  const mergedHandlers: Partial<Record<string, RouteHandlerFn>> = { ...DEFAULT_HANDLERS, ...config.handlers };

  const navigate = (url: string): void => {
    applyProviderChain(providers, url);
  };

  const handle = (async (action, meta, e) => {
    const handler = mergedHandlers[action];
    const route = routes[action];

    if (!handler && !route) return;

    // sync — a tag default action 차단. await 이전에 호출되어야 effective.
    e.preventDefault();

    // handler = side effect (log / analytics). throw 시 navigate skip.
    if (handler) {
      try {
        await handler(e, meta);
      } catch {
        return;
      }
    }

    if (!route) return;

    // action 식별자도 전달 — provider 가 action-별 전용 interface 호출 가능
    // (e.g., nolBridgeProvider 의 'logo' → NolBridge.home, 그 외 → NolBridge.openWebView).
    applyProviderChain(providers, route, action);
  }) as ActionHandler;

  handle.navigate = navigate;
  return handle;
}
